import AccountCircle from "@mui/icons-material/AccountCircle";
import {
    Button,
    CircularProgress,
    ClickAwayListener,
    Grow,
    IconButton,
    MenuItem,
    MenuList,
    AppBar as MuiAppBar,
    Paper,
    Popper,
    styled,
    Toolbar,
} from "@mui/material";
import { get, includes, map, toUpper } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { user_role_display_name } from "../../../app/js/auth/constants";
import { searchPropertyCategory } from "../../../app/js/search/constants";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { Search } from "../../../common/js/icons";
import "../../styles/components/AppBar.css";

const StyledMenuButton = styled(IconButton)({
    marginLeft: -12,
    padding: "4px",
    transition: "background-color 190ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
    },
});

const StyledAppBar = styled(MuiAppBar)({
    boxShadow: "none",
    borderBottom: "1px solid #e0e0e0",
    alignContent: "center",
    paddingBottom: "5px",
    backgroundColor: "white",
});

const StyledToolbar = styled(Toolbar)({
    maxHeight: "125px",
    color: "#9e9e9e",
});

const StyledSearchAdornment = styled(Search)({
    fill: "white",
    background: "#2C99D2",
    "&:hover": {
        cursor: "pointer",
    },
});

const StyledFilterSearch = styled(BSInput)({
    marginTop: 0,
    marginBottom: 0,
});

const StyledUserMenu = styled(Paper)({
    width: "16em",
    boxShadow: "2px 2px 5px 2px rgba(34,54,70,.1)",
    marginTop: "18px",
    maxHeight: "80vh",
    overflowY: "scroll",
});

const StyledMenuItem = styled(MenuItem)({
    padding: "2px 16px",
    margin: "0px",
    fontSize: "14px",
});

const StyledUserSwitchBack = styled(MenuItem)({
    padding: "2px 16px",
    margin: "0px",
    marginTop: "8px",
    fontSize: "14px",
});

const StyledDivider = styled(MenuItem)({
    padding: "0px 16px",
    margin: "0px",
    marginTop: "8px",
    fontSize: "14px",
    borderTop: "1px solid #e5e5e5",
    height: "0px",
});

const StyledUserMenuCategory = styled(MenuItem)({
    padding: "2px 16px",
    margin: "0px",
    fontSize: "14px",
    color: "#a0a0a0",
    "&:hover": {
        backgroundColor: "white",
        cursor: "default",
    },
});

const StyledProgressDiv = styled("div")({
    textAlign: "center",
    width: "40px",
    padding: "4px",
    display: "inline-block",
});

const StyledSearchBarRow = styled("div")({
    display: "flex",
    height: "65px",
    flexDirection: "column",
    flex: "0 0 41.666667%",
    maxWidth: "41.666667%",
    padding: "4px",
});

const StyledSearchTypeCategory = styled("div")({
    display: "flex",
    height: "65px",
    flexDirection: "column",
    marginTop: "-22px",
    minWidth: "200px",
    padding: "4px",
    boxSizing: "border-box",
});

const StyledContainer = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    margin: "-4px",
    width: "100%",
    boxSizing: "border-box",
});

const StyledItem = styled("div")({
    padding: "4px",
    boxSizing: "border-box",
});

const StyledSearchContainer = styled("div")({
    display: "flex",
    minWidth: "1000px",
    height: "65px",
    margin: "5px -4px -4px -4px",
    boxSizing: "border-box",
    flexWrap: "wrap",
});

const StyledGroupContainer = styled("div")({
    display: "flex",
    alignItems: "center",
    minWidth: "700px",
    height: "49px",
    margin: "-4px",
    boxSizing: "border-box",
    flexWrap: "wrap",
});

const StyledButtonContainer = styled("div")({
    display: "flex",
    height: "65px",
    boxSizing: "border-box",
    flexDirection: "column",
    padding: "4px",
});

const StyledUserContainer = styled("div")(({ onSearchPage, onGroupPage }) => ({
    marginTop: onSearchPage || onGroupPage ? "8px" : "0px",
    padding: "4px",
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
}));

const KMAppBar = (props) => {
    const {
        authenticatedUserProfile,
        compCurrentGroup,
        compSearchCriteria,
        compSearchRun,
        elementsSaving,
        getSavedGroupsForUser,
        handleSearchSubmit,
        listingCurrentGroup,
        listingSearchCriteria,
        listingSearchRun,
        onGroupPage,
        onSearchPage,
        propertySearchCriteria,
        propSearchRun,
        role,
        updateSearchTerm,
        userProfile,
        viewSavedSearches,
        toggleNameSearchModal,
        updateFilter,
        switchUserProfile,
        location,
    } = props;

    const anchorRef = useRef(null);
    const [pendingUser, setPendingUser] = useState(null);
    const [open, setOpen] = useState(false);
    const [searchCategory, setSearchCategory] = useState("GENERAL");
    const [searchPlaceholder, setSearchPlaceholder] = useState("General Search...");
    const [searchType, setSearchType] = useState("");
    const currentType = location.pathname.includes("listings")
        ? "LISTINGS"
        : location.pathname.includes("comps")
        ? "COMPS"
        : "PROPERTY";

    useEffect(() => {
        if (currentType !== searchType) {
            setSearchType(currentType);
            determinePlaceholderText(currentType, searchCategory);
        }
    }, [location.pathname, searchType, searchCategory]);

    const searchCriteria =
        currentType === "LISTINGS"
            ? listingSearchCriteria
            : currentType === "COMPS"
            ? compSearchCriteria
            : propertySearchCriteria;

    const searchRun =
        currentType === "LISTINGS" ? listingSearchRun : currentType === "COMPS" ? compSearchRun : propSearchRun;

    const currentGroup =
        currentType === "LISTINGS" ? listingCurrentGroup : currentType === "COMPS" ? compCurrentGroup : [];
    const showAssetServicesFilters = includes(
        ["propertyManagement", "transition", "accounting", "dataEntry", "dataAdmin"],
        role
    );

    const toggleSearchCategory = (e, searchCriteria) => {
        determinePlaceholderText(searchType, e.target.value);
        setSearchCategory(e.target.value);

        if (e.target.value === "ACCT_SSID") {
            toggleFilters(true);
        } else {
            toggleFilters(false);
        }

        updateSearchTerm(searchCriteria.searchTerm, e.target.value, toUpper(searchType));
    };

    const handleKeyPress = (key) => {
        if (key === "Enter") {
            handleSearchSubmit(searchType);
        }
    };

    const toggleFilters = (isToggled) => {
        updateFilter("statusCriteria.kmManagedOnly", isToggled);
        updateFilter("searchByASSId", isToggled);
    };

    const determinePlaceholderText = (searchType, searchCategory) => {
        let placeholderText;

        if (toUpper(searchType) === "PROPERTY") {
            if (searchCategory === "ACCT_SSID") {
                placeholderText = "Searching by Accounting Software System ID...";
            } else {
                placeholderText = "General Search...";
            }
        } else {
            placeholderText = "General Search...";
        }
        setSearchPlaceholder(placeholderText);
    };

    const handleToggle = () => {
        setOpen(!open);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const switchUser = (event, user) => {
        setPendingUser(user);
        handleClose(event);
    };

    return (
        <div style={{ position: "sticky", top: 0, zIndex: 3 }}>
            <StyledAppBar position="static">
                <StyledToolbar>
                    <StyledContainer>
                        <StyledItem>
                            {onSearchPage ? (
                                <StyledSearchContainer>
                                    {searchType === "PROPERTY" && showAssetServicesFilters && (
                                        <StyledSearchTypeCategory>
                                            <BSSelect
                                                id="searchTermCategory"
                                                value={searchCategory}
                                                onChange={(e) => toggleSearchCategory(e, searchCriteria)}
                                                options={searchPropertyCategory}
                                            />
                                        </StyledSearchTypeCategory>
                                    )}
                                    <StyledSearchBarRow>
                                        <StyledFilterSearch
                                            id="searchTerm"
                                            noHelperText
                                            value={searchCriteria.searchTerm}
                                            onChange={(e) =>
                                                updateSearchTerm(e.target.value, searchType, searchCategory)
                                            }
                                            placeholder={searchPlaceholder}
                                            onKeyPress={(e) => handleKeyPress(e.key)}
                                            endAdornment={
                                                <div className="filledSearch" data-testid="filledSearch">
                                                    <StyledSearchAdornment
                                                        onClick={() => handleSearchSubmit(searchType, searchCategory)}
                                                    />
                                                </div>
                                            }
                                        />
                                    </StyledSearchBarRow>
                                    {searchType !== "PROPERTY" && (
                                        <React.Fragment>
                                            <StyledButtonContainer>
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    disabled={searchRun ? false : true}
                                                    onClick={() => toggleNameSearchModal(true)}
                                                >
                                                    Save Search
                                                </Button>
                                            </StyledButtonContainer>
                                            <StyledButtonContainer>
                                                <Button
                                                    color="primary"
                                                    variant="outlined"
                                                    onClick={() =>
                                                        viewSavedSearches(searchType, userProfile.userAccountKey)
                                                    }
                                                >
                                                    View Saved Searches
                                                </Button>
                                            </StyledButtonContainer>{" "}
                                        </React.Fragment>
                                    )}
                                </StyledSearchContainer>
                            ) : onGroupPage ? (
                                <StyledGroupContainer>
                                    <div
                                        style={{
                                            flex: "0 0 41.666667%",
                                            maxWidth: "41.666667%",
                                            padding: "0 8px",
                                            marginBottom: "-17px",
                                        }}
                                    >
                                        <StyledFilterSearch
                                            disabled={true}
                                            id="searchTerm"
                                            value={searchCriteria.searchTerm}
                                            onChange={(e) => updateSearchTerm(e.target.value, searchType)}
                                            placeholder="Search disabled in list view"
                                            endAdornment={
                                                <div className="filledSearch">
                                                    <StyledSearchAdornment />
                                                </div>
                                            }
                                        />
                                    </div>
                                    <div style={{ textAlign: "center", padding: "0 8px" }}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            disabled={currentGroup && currentGroup.length > 0 ? false : true}
                                            onClick={() => toggleNameSearchModal(true)}
                                        >
                                            Save List
                                        </Button>
                                    </div>
                                    <div style={{ flex: "0 0 33.333333%", maxWidth: "33.333333%", padding: "0 8px" }}>
                                        <Button
                                            color="primary"
                                            variant="outlined"
                                            onClick={() =>
                                                getSavedGroupsForUser(searchType, userProfile.userAccountKey)
                                            }
                                        >
                                            View Saved Lists
                                        </Button>
                                    </div>
                                </StyledGroupContainer>
                            ) : (
                                ""
                            )}
                        </StyledItem>
                        <StyledUserContainer onSearchPage={onSearchPage} onGroupPage={onGroupPage}>
                            <div>
                                <div style={{ float: "left" }}>
                                    {elementsSaving.switchUser ? (
                                        <StyledProgressDiv>
                                            <CircularProgress size={20} color="primary" />
                                        </StyledProgressDiv>
                                    ) : (
                                        <StyledMenuButton
                                            color="inherit"
                                            aria-label="Menu"
                                            data-testid="proxyMenu"
                                            ref={anchorRef}
                                            onClick={handleToggle}
                                        >
                                            <AccountCircle style={{ fontSize: "32px" }} />
                                        </StyledMenuButton>
                                    )}
                                    <Popper
                                        open={open}
                                        anchorEl={anchorRef.current}
                                        transition
                                        disablePortal
                                        placement="bottom-start"
                                    >
                                        {({ TransitionProps, placement }) => (
                                            <Grow
                                                {...TransitionProps}
                                                id="menu-list-grow"
                                                unmountOnExit
                                                onExited={() => {
                                                    if (pendingUser) {
                                                        switchUserProfile(pendingUser);
                                                        setPendingUser(null);
                                                    }
                                                }}
                                            >
                                                <div>
                                                    <ClickAwayListener onClickAway={handleClose}>
                                                        <StyledUserMenu>
                                                            <MenuList>
                                                                {authenticatedUserProfile.user !== userProfile.user && (
                                                                    <React.Fragment>
                                                                        <StyledUserSwitchBack
                                                                            onClick={(e) =>
                                                                                switchUser(
                                                                                    e,
                                                                                    authenticatedUserProfile.user
                                                                                )
                                                                            }
                                                                        >{`Switch back to ${authenticatedUserProfile.firstName} ${authenticatedUserProfile.lastName}`}</StyledUserSwitchBack>
                                                                        <StyledDivider />
                                                                    </React.Fragment>
                                                                )}
                                                                <StyledUserMenuCategory>
                                                                    Become Proxy For:
                                                                </StyledUserMenuCategory>
                                                                {map(
                                                                    get(
                                                                        authenticatedUserProfile,
                                                                        "allowedUserProfiles",
                                                                        []
                                                                    ),
                                                                    (user, index) => {
                                                                        return (
                                                                            <StyledMenuItem
                                                                                key={`userProxy${index}`}
                                                                                id={`userProxy${index}`}
                                                                                onClick={(e) =>
                                                                                    switchUser(e, user.proxyFor)
                                                                                }
                                                                            >
                                                                                {user.name}
                                                                            </StyledMenuItem>
                                                                        );
                                                                    }
                                                                )}
                                                            </MenuList>
                                                        </StyledUserMenu>
                                                    </ClickAwayListener>
                                                </div>
                                            </Grow>
                                        )}
                                    </Popper>
                                </div>
                                <div
                                    style={{
                                        float: "right",
                                        color: "black",
                                        paddingLeft: "8px",
                                        marginRight: "20px",
                                        minWidth: "12em",
                                    }}
                                >
                                    <div
                                        style={
                                            authenticatedUserProfile.user === userProfile.user
                                                ? { paddingTop: "10px", display: "block", fontSize: "14px" }
                                                : { display: "block", fontSize: "14px" }
                                        }
                                    >
                                        <span id="authenticatedUserName" style={{ marginRight: "6px" }}>
                                            {authenticatedUserProfile.firstName} {authenticatedUserProfile.lastName}
                                        </span>
                                        <span
                                            style={{
                                                fontWeight: 500,
                                                color: "#a0a0a0",
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {user_role_display_name[authenticatedUserProfile.role]}
                                        </span>
                                    </div>
                                    {authenticatedUserProfile.user !== userProfile.user && (
                                        <div style={{ display: "block", fontSize: "14px" }}>
                                            <span id="proxyUserName" style={{ marginRight: "6px" }}>
                                                Proxy For: {userProfile.firstName} {userProfile.lastName}
                                            </span>
                                            <span
                                                style={{
                                                    fontWeight: 500,
                                                    color: "#a0a0a0",
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                {user_role_display_name[userProfile.role]}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </StyledUserContainer>
                    </StyledContainer>
                </StyledToolbar>
            </StyledAppBar>
        </div>
    );
};

KMAppBar.propTypes = {
    authenticatedUserProfile: PropTypes.object.isRequired,
    compCurrentGroup: PropTypes.array,
    compSearchCriteria: PropTypes.object,
    compSearchRun: PropTypes.bool,
    elementsSaving: PropTypes.object,
    getSavedGroupsForUser: PropTypes.func,
    handleSearchSubmit: PropTypes.func,
    listingCurrentGroup: PropTypes.array,
    listingSearchCriteria: PropTypes.object,
    listingSearchRun: PropTypes.bool,
    onGroupPage: PropTypes.bool,
    onSearchPage: PropTypes.bool,
    propertySearchCriteria: PropTypes.object,
    propSearchRun: PropTypes.bool,
    role: PropTypes.string,
    updateSearchTerm: PropTypes.func,
    userProfile: PropTypes.object.isRequired,
    viewSavedSearches: PropTypes.func,
    toggleNameSearchModal: PropTypes.func,
    updateFilter: PropTypes.func,
    switchUserProfile: PropTypes.func,
    location: PropTypes.object.isRequired,
};

export default KMAppBar;
